/**
 * Landing — above-the-fold marketing page (v3).
 *
 * Copy grounded in Aakash Gupta's PM AI-challenges framework. Three problems
 * the tool actually solves: compute cost, hallucination cost, model choice.
 * Every problem card terminates with a terracotta "solves with" line that
 * names the exact output section that addresses it — closing the loop between
 * problem and tool.
 */
import { motion } from "framer-motion";
import { CornerDownRight } from "lucide-react";

const PROBLEMS = [
  {
    title: "Computational costs you did not model",
    body: "Most steps in your feature do not need AI. The ones that do compound fast at scale. Nobody maps this before building.",
    solves: "Feature flow shows exactly where the cost lives",
  },
  {
    title: "Hallucination you find in production",
    body: "A 2% error rate sounds small. At 80 calls per day that is 57 wrong outputs per month with a real dollar cost attached.",
    solves: "Costed failure mode quantifies it before you build",
  },
  {
    title: "A model choice you cannot defend",
    body: "Claude Opus costs 20× more than Haiku. For classification tasks they perform nearly identically. Most PMs do not know this.",
    solves: "Model recommendation with explicit reasoning",
  },
];

// --- Preview cards ---

const Eyebrow = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.18em] text-[#56534A] mb-3">{children}</div>
);

const FlowPreviewCard = () => {
  const pills = [
    { label: "Pull data", type: "local" },
    { label: "Cache", type: "cacheable" },
    { label: "Analyse", type: "api" },
    { label: "Format", type: "local" },
  ];
  const COLOR = {
    local: { border: "#DDD9CF", bg: "#FFFFFF", text: "#1A1814" },
    cacheable: { border: "#E8A930", bg: "#FBF1DC", text: "#7A5A12" },
    api: { border: "#C4521E", bg: "#FBE4D8", text: "#7A2E0C" },
  };
  return (
    <div className="border border-[#DDD9CF] bg-white rounded-md p-5 xl:p-6 min-h-[200px]" data-testid="preview-flow">
      <Eyebrow>Feature flow</Eyebrow>
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        {pills.map((p, i) => {
          const c = COLOR[p.type];
          return (
            <div key={p.label} className="flex items-center">
              <span
                className="text-xs px-2.5 py-1 border rounded font-medium"
                style={{ borderColor: c.border, backgroundColor: c.bg, color: c.text }}
              >
                {p.label}
              </span>
              {i < pills.length - 1 && <span className="text-[#9B9589] text-xs px-1">→</span>}
            </div>
          );
        })}
      </div>
      <p className="text-sm text-[#56534A] mt-4 leading-relaxed">
        Which steps are free. Which steps cost money.
      </p>
    </div>
  );
};

const CostedFailureCard = () => (
  <div className="border border-[#DDD9CF] bg-white rounded-md p-5 xl:p-6 min-h-[200px]" data-testid="preview-failure">
    <Eyebrow>Costed failure mode</Eyebrow>
    <div className="border-l-2 border-[#C4521E] bg-[#FBE4D8]/40 rounded-r pl-3 py-2 pr-3 mt-2">
      <h4 className="font-serif-display text-base text-[#1A1814]">Confident misidentification</h4>
      <p className="text-sm text-[#1A1814] italic mt-2 leading-relaxed">
        At 2% on 80 calls/day — $384/month in wrong orders. That is 27% of the feature's projected value.
      </p>
    </div>
    <p className="text-xs text-[#56534A] mt-3 leading-relaxed">
      What goes wrong, and what it costs.
    </p>
  </div>
);

const ModelRecommendationCard = () => (
  <div className="border border-[#DDD9CF] bg-white rounded-md p-5 xl:p-6 min-h-[200px]" data-testid="preview-model">
    <Eyebrow>Model recommendation</Eyebrow>
    <div className="space-y-2 mt-2">
      <div
        className="flex items-baseline justify-between border border-[#C4521E] bg-[#FBE4D8]/40 rounded px-3 py-2"
        data-testid="model-pick"
      >
        <div>
          <div className="font-serif-display text-base text-[#1A1814]">Claude Haiku</div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#C4521E] mt-0.5">
            Recommended
          </div>
        </div>
        <div className="text-xs text-[#56534A] tabular-nums">$0.25/M in</div>
      </div>
      <div className="flex items-baseline justify-between border border-[#DDD9CF] rounded px-3 py-2">
        <div className="font-serif-display text-sm text-[#56534A] line-through opacity-70">
          Claude Opus
        </div>
        <div className="text-xs text-[#56534A] tabular-nums">$15.00/M in · 20×</div>
      </div>
    </div>
    <p className="text-xs text-[#56534A] mt-3 leading-relaxed italic">
      Classification, not reasoning. Haiku matches Opus on this task at 1/20 the cost.
    </p>
  </div>
);

const RippleCard = () => (
  <div className="border border-[#DDD9CF] bg-white rounded-md p-5 xl:p-6 min-h-[200px]" data-testid="preview-ripple">
    <Eyebrow>The ripple</Eyebrow>
    <p className="font-serif-display italic text-[#1A1814] text-sm leading-relaxed mt-2">
      What this feature makes possible next.
    </p>
    <div className="mt-3 space-y-2">
      {[
        "Mechanic onboarding at scale",
        "Fleet manager self-service",
        "Condition assessment layer",
      ].map((t, i) => (
        <div key={t} className="flex items-baseline gap-3 text-sm">
          <span className="text-[#C4521E] text-xs font-medium tabular-nums">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="text-[#1A1814]">{t}</span>
        </div>
      ))}
    </div>
  </div>
);

// --- Layout ---

const SectionEyebrow = ({ children, testid }) => (
  <div
    className="text-[10px] uppercase tracking-[0.22em] text-[#56534A] mb-6"
    data-testid={testid}
  >
    {children}
  </div>
);

export const Landing = ({ onBeginScroll, onWatchDemo }) => (
  <div className="min-h-screen flex flex-col bg-[#F9F7F3]" data-testid="landing-hero">
    {/* Top nav */}
    <nav className="border-b border-[#DDD9CF]">
      <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto px-6 md:px-12 xl:px-16 py-5 flex items-center justify-between">
        <span className="font-serif-display text-xl text-[#1A1814]" data-testid="nav-logo">
          SuperHumanly
        </span>
        <button
          type="button"
          onClick={onWatchDemo}
          className="bg-[#1A1814] text-[#F9F7F3] hover:bg-[#000000] px-5 py-2 text-sm transition-colors flex items-center gap-2 rounded"
          data-testid="nav-watch-demo"
        >
          <span aria-hidden="true">▶</span>
          Watch the story
        </button>
      </div>
    </nav>

    <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto px-6 md:px-12 xl:px-16 py-14 md:py-20 w-full">
      {/* Hero */}
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-[10px] uppercase tracking-[0.22em] text-[#C4521E] mb-6"
        data-testid="audience-line"
      >
        For product managers shipping AI features
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.05 }}
        className="font-serif-display text-4xl md:text-5xl xl:text-6xl 2xl:text-7xl tracking-tight leading-[1.05] max-w-4xl"
        data-testid="enemy-headline"
      >
        <span className="text-[#1A1814]">Three things kill AI features before they launch.</span>
        <br />
        <span className="text-[#56534A]">See all three before you commit to build.</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mt-7 text-[#1A1814] text-base xl:text-lg max-w-2xl leading-relaxed"
        data-testid="hero-lead"
      >
        <span className="font-medium">Unexpected compute costs.</span>{" "}
        <span className="text-[#56534A]">
          Hallucination you only discover in production. A model choice you cannot defend to your CEO.
        </span>
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-4 text-[#56534A] text-sm xl:text-base max-w-2xl leading-relaxed"
      >
        SuperHumanly maps your feature step by step — showing where AI is actually needed, what it
        will cost at scale, and what failure looks like as a costed scenario.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-8 flex flex-wrap items-center gap-3"
      >
        <button
          type="button"
          onClick={onBeginScroll}
          className="bg-[#C4521E] text-[#F9F7F3] hover:bg-[#a34418] px-6 py-3 transition-colors text-sm flex items-center gap-2 rounded font-medium"
          data-testid="hero-begin-button"
        >
          Try it with your product <span aria-hidden="true">→</span>
        </button>
        <button
          type="button"
          onClick={onWatchDemo}
          className="bg-[#1A1814] text-[#F9F7F3] hover:bg-[#000000] px-6 py-3 transition-colors text-sm flex items-center gap-2 rounded"
          data-testid="hero-demo-button"
        >
          <span aria-hidden="true">▶</span>
          Watch the 75-second story
        </button>
      </motion.div>

      {/* Rule */}
      <div className="hr-ink mt-16 mb-12" />

      {/* Problems */}
      <SectionEyebrow testid="problems-eyebrow">The three problems this solves</SectionEyebrow>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="border border-[#DDD9CF] bg-white rounded-md grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#DDD9CF]"
        data-testid="problem-scenarios"
      >
        {PROBLEMS.map((p, i) => (
          <div
            key={p.title}
            className="p-6 xl:p-8 flex flex-col"
            data-testid={`problem-${i}`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#C4521E] mb-3 font-medium">
              {String(i + 1).padStart(2, "0")}
            </div>
            <h3 className="font-serif-display text-xl xl:text-2xl text-[#1A1814] leading-tight">
              {p.title}
            </h3>
            <p className="mt-3 text-[#56534A] text-sm xl:text-base leading-relaxed flex-1">
              {p.body}
            </p>
            <p
              className="mt-5 text-[#C4521E] text-sm font-medium leading-relaxed flex items-start gap-2"
              data-testid={`problem-${i}-solves`}
            >
              <CornerDownRight size={14} strokeWidth={1.8} className="mt-1 shrink-0" />
              <span>{p.solves}</span>
            </p>
          </div>
        ))}
      </motion.div>

      {/* Rule */}
      <div className="hr-ink mt-16 mb-12" />

      {/* Preview */}
      <SectionEyebrow testid="preview-eyebrow">What you walk away with</SectionEyebrow>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        data-testid="output-preview"
      >
        <FlowPreviewCard />
        <CostedFailureCard />
        <ModelRecommendationCard />
        <RippleCard />
      </motion.div>

      {/* Rule */}
      <div className="hr-ink mt-16 mb-10" />

      {/* Credibility */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-sm text-[#56534A] italic max-w-2xl leading-relaxed border-l-2 border-[#C4521E]/40 pl-4"
        data-testid="credibility-line"
      >
        Built by a product manager who got the cost estimate wrong, discovered hallucination in
        production, and wished this existed before they built it.
      </motion.p>
    </div>
  </div>
);
